<script setup lang="ts">
import { computed, defineProps, withDefaults } from "vue";
import dayjs from "dayjs";
import type { ContentItem } from "@edco/apollo";
import { adaptSize } from "~/utils/sizeAdapter";
import { formatNames } from "~/utils/stringUtils";
import type { AuthorItem } from "~/models/generated/AuthorItem";

const props = withDefaults(
  defineProps<{
    name: string;
    hideLabels?: boolean;
    url?: string;
    associatedAuthors?: ContentItem<AuthorItem>[] | null;
    authorName?: string | null;
    searchThumbnail?: { url: string } | null;
    bannerImage?: { url: string } | null;
    orgName?: string | null;
    publishedDate?: string | Date | null;
    time?: number;
    topics?: Array<string>;
    hideTags?: boolean;
    maxTags?: number | string | null;
    growImage?: boolean;
    nameSize?: string;
    dynamicNameSize?: boolean;
    noFooter?: boolean;
    showStats?: boolean;
    viewCount?: number;
    kudosCount?: number;
    responseCount?: number;
    mostPopular?: boolean;
    shadow?: boolean;
    eventDate?: string | null;
    eventTime?: string | null;
    eventLocation?: string | null;
    authorIntroduction?: string | null;
    multipleAuthors?: boolean;
  }>(),
  {
    hideLabels: false,
    url: "#",
    authorName: null,
    associatedAuthors: null,
    searchThumbnail: null,
    bannerImage: null,
    orgName: null,
    publishedDate: null,
    time: 0,
    topics: () => [],
    hideTags: false,
    maxTags: null,
    growImage: false,
    nameSize: "md",
    dynamicNameSize: false,
    noFooter: false,
    showStats: false,
    viewCount: 0,
    kudosCount: 0,
    responseCount: 0,
    mostPopular: false,
    shadow: false,
    eventDate: null,
    eventTime: null,
    eventLocation: null,
    authorIntroduction: null,
    multipleAuthors: false,
  },
);

const displayNewLabel = computed( () =>
  !props.hideLabels && props.publishedDate && isArticleNewlyListed( props.publishedDate ),
);

const renderNameSize = computed( () =>
  props.dynamicNameSize ? adaptSize( props.name, props.nameSize ) : props.nameSize,
);

const hasTopics = computed( () => Array.isArray( props.topics ) && props.topics.length > 0 );

const thumbnail = computed( () => {
  if ( props.searchThumbnail ) {
    return props.searchThumbnail.url;
  }

  if ( props.bannerImage && props.bannerImage.url ) {
    return props.bannerImage.url;
  }

  return null;
} );

const authors = computed( () => props.associatedAuthors || [] );

const resolvedAuthorName = computed( () => {
  return props.authorName || formatNames( authors.value.map( a => a.name ) );
} );

const formattedEventDate = computed( () => {
  return props.eventDate
    ? dayjs( props.eventDate ).format( "DD/MM/YYYY" )
    : null;
} );

const formattedEventTime = computed( () => {
  return props.eventTime
    ? dayjs( props.eventTime ).format( "HH:mm" )
    : null;
} );
</script>

<template>
  <nuxt-link
    :to="url || '#'"
    class="article-card"
    :class="{ 'article-card--shadow': shadow }">
    <div class="article-card__wrapper">
      <div class="article-card__content-wrapper">
        <div class="article-card__content">
          <h3 :class="`article-card__heading article-card__heading--${renderNameSize}`">
            {{ name }}
          </h3>

          <div
            v-if="!noFooter"
            class="article-card__footer">
            <template v-if="showStats">
              <div class="article-card__stats">
                <div class="article-card__stat">
                  <span class="article-card__stat-number">
                    {{ viewCount || 0 }}
                  </span>
                  <span class="article-card__stat-description">
                    Views
                  </span>
                </div>

                <div class="article-card__stat">
                  <span class="article-card__stat-number">
                    {{ kudosCount || 0 }}
                  </span>
                  <span class="article-card__stat-description">
                    Kudos
                  </span>
                </div>

                <div class="article-card__stat">
                  <span class="article-card__stat-number">
                    {{ responseCount || 0 }}
                  </span>
                  <span class="article-card__stat-description">
                    Responses
                  </span>
                </div>
              </div>
            </template>

            <div
              v-if="formattedEventDate || formattedEventTime || eventLocation"
              class="event-info">
              <span v-if="formattedEventDate">
                <a-icon icon="fa:light:calendar-days" />
                {{ formattedEventDate }}
              </span>
              <span v-if="formattedEventTime">
                <a-icon icon="fa:light:clock" />
                {{ formattedEventTime }}
              </span>
              <span v-if="eventLocation">
                <a-icon icon="fa:light:building" />
                {{ eventLocation }}
              </span>
            </div>

            <template v-if="!hideTags && hasTopics">
              <tag-list
                size="sm"
                class="article-card__tags"
                :tags="topics"
                :max="maxTags" />
            </template>

            <div class="article-card__actions">
              <span class="article-card__action">
                <a-icon icon="fa:light:chevron-right" />
                View event
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        class="article-card__image-wrapper"
        :class="growImage ? 'article-card__image-wrapper--dynamic' : ''">
        <NuxtImg
          v-if="thumbnail"
          :src="`${thumbnail}?width=600&format=webp`"
          alt=""
          role="presentation"
          class="article-card__image" />

        <div class="article-card__image-overlay">
          <info-label
            v-if="displayNewLabel"
            no-text-shadow
            class="article-card__new"
            label="New Event" />

          <most-pop-badge
            v-if="mostPopular"
            class="article-card__most-popular" />

          <span
            v-if="orgName"
            class="article-card__org">
            {{ orgName }}
          </span>

          <span
            v-if="resolvedAuthorName"
            class="article-card__author">
            <template v-if="authorIntroduction">{{ authorIntroduction + ' ' }}</template>{{ resolvedAuthorName }}
          </span>
        </div>
      </div>
    </div>
  </nuxt-link>
</template>

<style lang="scss" scoped>
@use "sass:color";

@import '~/assets/styles/_variables.scss';

.article-card {
  background: #ffffff;
  border-radius: 7px;
  border: 1px solid #f2f2f2;
  display: flex;
  position: relative;
  flex-direction: column;
  text-decoration: none;
  flex-grow: 1;
  height: 100%;
  container-type: inline-size;
  transition: transform 0.2s ease-in, box-shadow 0.2s ease-in;

  &:hover {
    transform: translateY( -0.25rem );
  }

  &--shadow,
  &:hover {
    box-shadow: 0 5px 10px rgba(68, 68, 68, 0.25);
  }

  &__wrapper {
    display: flex;
    flex-direction: column-reverse;
    height: 100%;
  }

  &__image-wrapper {
    background: #555;
    position: relative;
    display: flex;
    min-height: 200px;
  }

  &__image {
    flex-grow: 1;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 7px 7px 0 0;
  }

  &__image-overlay {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    position: relative;
    z-index: 10;
    color: #fff;
    padding: 15px calc( 2.5rem + 20px ) 15px 15px;
    background-image: linear-gradient( 0deg,
      rgba( 27, 28, 34, 0.8 ) 0%,
      rgba( 27, 28, 34, 0.7 ) 15%,
      rgba( 27, 28, 34, 0.6 ) 33%,
      rgba( 27, 28, 34, 0 ) 100% );
    border-radius: 7px 7px 0 0;
    text-shadow: 0 0 4px #000000d1;
  }

  &__image-wrapper,
  &__image,
  &__image-overlay {
    border-radius: 6px 6px 0 0;
  }

  &__content-wrapper {
    overflow: hidden;
    position: relative;
    flex-grow: 1;
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding: 15px;
    color: #201D25;
    height: 100%;
  }

  &__heading {
    font-size: 1.1rem;
    font-family: var( --font-family-base ), sans-serif;
    margin-bottom: 15px;

    &--xs {
      font-size: 0.8rem;
    }

    &--sm {
      font-size: 1rem;
    }

    &--lg {
      font-size: 1.25rem;
    }

    &--xl {
      font-size: 1.5rem;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__org {
    font-size: 0.8rem;
  }

  &__footer {
    margin-top: auto;
  }

  &__tags {
    margin-bottom: 10px;
  }

  &__actions {
    border-top: 1px solid var( --primary );
    display: flex;
    justify-content: space-between;
    padding-top: 15px;
    align-items: center;
  }

  &__action {
    font-size: 0.875rem;
    line-height: 0.875rem;

    svg {
      font-size: 0.5rem;
    }
  }

  &__author {
    word-break: break-word;
  }

  &__new {
    z-index: 11;
    position: absolute;
    top: 10px;
    left: 10px;
  }

  &__most-popular {
    position: absolute;
    top: -10px;
    right: 0;
    z-index: 60;
  }

  &__stats {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 5px;
    background: #f6f9fc;
    padding: 7px 10px;
    font-size: 0.85rem;
    margin-bottom: 5px;
  }

  &__stat-number {
    font-weight: 600;
  }

  @container (min-width: 600px) {
    &__wrapper {
      flex-direction: row-reverse;
      justify-content: stretch;
    }

    &__image-wrapper,
    &__content-wrapper {
      flex-basis: 50%;
    }

    &__image-wrapper,
    &__image,
    &__image-overlay {
      border-radius: 7px 0 0 7px;
    }
  }
}

.dark-mode .article-card {
  background: #2a2a33;
  border-color: #3f3f4d;

  &__stats {
    background: #070e35;
  }

  &:hover,
  &--shadow {
    box-shadow: 0 5px 10px 0 rgb( 11, 12, 14, 0.5 );
  }
}

.event-info {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 0.875rem;
  margin-bottom: 10px;

  span {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .a-icon {
    flex-shrink: 0;
  }
}
</style>
